import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { productApi, partnerVendorApi, fileApi } from '../api';
import { ITEM_CATEGORIES, PRODUCT_TYPES, PRODUCT_TYPE_COLORS } from '../constants';

const PRODUCT_STATUSES = [
  { value: 'SALE', label: '판매중' },
  { value: 'STOP', label: '판매중지' },
  { value: 'SOLDOUT', label: '품절' },
];

const PRODUCT_STATUS_COLORS: Record<string, string> = {
  SALE: 'bg-green-100 text-green-700',
  STOP: 'bg-gray-100 text-gray-700',
  SOLDOUT: 'bg-red-100 text-red-700',
};

interface Product {
  productIdx: number;
  productCode?: string;
  productType?: string;
  name: string;
  description?: string;
  category?: string;
  costPrice?: number;
  salesPrice?: number;
  rentalPrice?: number;
  depositAmount?: number;
  packagePrice?: number;
  stockQuantity?: number;
  totalStock?: number;
  status?: string;
  thumbnailUrl?: string;
  createdDt?: string;
  packageItems?: any[];
}

const INITIAL_FORM = {
  productType: 'SALE' as string,
  name: '',
  description: '',
  category: 'SUIT',
  costPrice: 0,
  salesPrice: 0,
  rentalPrice: 0,
  depositAmount: 0,
  packagePrice: 0,
  stockQuantity: 0,
  totalStock: 0,
  status: 'SALE',
  thumbnailUrl: '',
  packageItems: [] as Array<{ componentProductIdx: number; quantity: number; isRequired: string; name?: string }>,
};

export default function SuitProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('');

  // 모달
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form, setForm] = useState(INITIAL_FORM);
  // 업체 구분
  const [productOwnerType, setProductOwnerType] = useState<'SELF' | 'PARTNER'>('SELF');
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerResults, setPartnerResults] = useState<any[]>([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string>('');

  // 패키지 구성 상품 선택용
  const [componentSearchQuery, setComponentSearchQuery] = useState('');
  const [componentSearchResults, setComponentSearchResults] = useState<Product[]>([]);
  const [showComponentDropdown, setShowComponentDropdown] = useState(false);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await productApi.list({
        q: searchQuery || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        productType: productTypeFilter || undefined,
        page,
        size: 20,
      });
      setProducts(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('상품 목록 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, categoryFilter, statusFilter, productTypeFilter, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // 파트너 업체 검색
  useEffect(() => {
    if (productOwnerType !== 'PARTNER' || !showModal) return;
    const timer = setTimeout(async () => {
      try {
        const data = await partnerVendorApi.list({ q: partnerQuery || undefined, size: 20 });
        setPartnerResults(data.items || data || []);
      } catch {
        setPartnerResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [partnerQuery, productOwnerType, showModal]);

  // 패키지 구성 상품 검색
  useEffect(() => {
    if (form.productType !== 'PACKAGE' || !componentSearchQuery.trim()) {
      setComponentSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await productApi.list({ q: componentSearchQuery, size: 20 });
        // 패키지 상품 제외, 이미 추가된 상품 제외
        const addedIdxs = form.packageItems.map(i => i.componentProductIdx);
        setComponentSearchResults(
          (data.items || []).filter((p: Product) => p.productType !== 'PACKAGE' && !addedIdxs.includes(p.productIdx))
        );
      } catch {
        setComponentSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [componentSearchQuery, form.productType, form.packageItems]);

  const openModal = async (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      // 패키지 상품이면 구성 상품 목록 로드
      let packageItems: any[] = [];
      if (product.productType === 'PACKAGE') {
        try {
          const detail = await productApi.detail(product.productIdx);
          packageItems = (detail.packageItems || []).map((pi: any) => ({
            componentProductIdx: pi.componentProduct?.productIdx,
            quantity: pi.quantity,
            isRequired: pi.isRequired,
            name: pi.componentProduct?.name,
            productType: pi.componentProduct?.productType,
          }));
        } catch { /* ignore */ }
      }
      setForm({
        productType: product.productType || 'SALE',
        name: product.name || '',
        description: product.description || '',
        category: product.category || 'SUIT',
        costPrice: product.costPrice || 0,
        salesPrice: product.salesPrice || 0,
        rentalPrice: product.rentalPrice || 0,
        depositAmount: product.depositAmount || 0,
        packagePrice: product.packagePrice || 0,
        stockQuantity: product.stockQuantity || 0,
        totalStock: product.totalStock || 0,
        status: product.status || 'SALE',
        thumbnailUrl: product.thumbnailUrl || '',
        packageItems,
      });
      setImagePreview(product.thumbnailUrl || '');
      setProductOwnerType('SELF');
      setSelectedPartner(null);
    } else {
      setEditingProduct(null);
      setForm(INITIAL_FORM);
      setImagePreview('');
      setProductOwnerType('SELF');
      setSelectedPartner(null);
    }
    setPartnerQuery('');
    setShowPartnerDropdown(false);
    setComponentSearchQuery('');
    setComponentSearchResults([]);
    setShowComponentDropdown(false);
    setShowModal(true);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('이미지 파일만 업로드 가능합니다.'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('10MB 이하의 이미지만 업로드 가능합니다.'); return; }

    const reader = new FileReader();
    reader.onload = (ev) => { setImagePreview(ev.target?.result as string); };
    reader.readAsDataURL(file);

    setIsUploading(true);
    try {
      const url = await fileApi.upload(file, 'product', 'suit-shop');
      setForm(prev => ({ ...prev, thumbnailUrl: url }));
    } catch (err) {
      console.error('이미지 업로드 실패:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const addPackageItem = (product: Product) => {
    setForm(prev => ({
      ...prev,
      packageItems: [...prev.packageItems, {
        componentProductIdx: product.productIdx,
        quantity: 1,
        isRequired: 'y',
        name: product.name,
        productType: product.productType,
      }],
    }));
    setComponentSearchQuery('');
    setComponentSearchResults([]);
    setShowComponentDropdown(false);
  };

  const removePackageItem = (idx: number) => {
    setForm(prev => ({
      ...prev,
      packageItems: prev.packageItems.filter((_, i) => i !== idx),
    }));
  };

  const updatePackageItemQuantity = (idx: number, quantity: number) => {
    setForm(prev => ({
      ...prev,
      packageItems: prev.packageItems.map((item, i) => i === idx ? { ...item, quantity } : item),
    }));
  };

  const togglePackageItemRequired = (idx: number) => {
    setForm(prev => ({
      ...prev,
      packageItems: prev.packageItems.map((item, i) => i === idx ? { ...item, isRequired: item.isRequired === 'y' ? 'n' : 'y' } : item),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { alert('상품명은 필수입니다.'); return; }
    if (productOwnerType === 'PARTNER' && !selectedPartner) {
      alert('파트너 업체를 선택해주세요.');
      return;
    }
    if (form.productType === 'PACKAGE' && form.packageItems.length === 0) {
      alert('패키지 상품에는 최소 1개 이상의 구성 상품이 필요합니다.');
      return;
    }

    const payload: any = {
      productType: form.productType,
      name: form.name,
      description: form.description,
      category: form.category,
      costPrice: form.costPrice,
      status: form.status,
      thumbnailUrl: form.thumbnailUrl,
      ...(productOwnerType === 'PARTNER' && selectedPartner
        ? { partnerVendorIdx: selectedPartner.vendorIdx }
        : {}),
    };

    // 유형별 필드
    if (form.productType === 'SALE') {
      payload.salesPrice = form.salesPrice;
      payload.stockQuantity = form.stockQuantity;
    } else if (form.productType === 'RENTAL') {
      payload.rentalPrice = form.rentalPrice;
      payload.depositAmount = form.depositAmount;
      payload.totalStock = form.totalStock;
    } else if (form.productType === 'PACKAGE') {
      payload.packagePrice = form.packagePrice;
      payload.packageItems = form.packageItems.map((item, idx) => ({
        componentProductIdx: item.componentProductIdx,
        quantity: item.quantity,
        isRequired: item.isRequired,
        sortOrder: idx,
      }));
    }

    try {
      if (editingProduct) {
        await productApi.update(editingProduct.productIdx, payload);
        alert('상품이 수정되었습니다.');
      } else {
        await productApi.create(payload);
        alert('상품이 등록되었습니다.');
      }
      setShowModal(false);
      fetchProducts();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDelete = async (product: Product) => {
    if (!confirm(`"${product.name}" 상품을 삭제하시겠습니까?`)) return;
    try {
      await productApi.delete(product.productIdx);
      alert('삭제되었습니다.');
      fetchProducts();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  const totalPages = Math.ceil(total / 20);

  // 가격 표시 헬퍼
  const getDisplayPrice = (p: Product) => {
    if (p.productType === 'RENTAL') return p.rentalPrice || 0;
    if (p.productType === 'PACKAGE') return p.packagePrice || 0;
    return p.salesPrice || 0;
  };
  const getPriceLabel = (p: Product) => {
    if (p.productType === 'RENTAL') return '대여료';
    if (p.productType === 'PACKAGE') return '패키지가';
    return '판매가';
  };
  const getStockDisplay = (p: Product) => {
    if (p.productType === 'RENTAL') return `보유 ${p.totalStock ?? 0}벌`;
    if (p.productType === 'PACKAGE') return '패키지';
    return `재고 ${p.stockQuantity ?? 0}`;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">상품관리</h1>
            <p className="text-sm text-gray-600 mt-1">예복샵 상품을 등록하고 관리합니다</p>
          </div>
          <button onClick={() => openModal()}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 cursor-pointer">
            <i className="ri-add-line"></i> 상품 등록
          </button>
        </div>

        {/* 필터 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">상품유형</label>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => { setProductTypeFilter(''); setPage(1); }}
                  className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                    productTypeFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>전체</button>
                {PRODUCT_TYPES.map(t => (
                  <button key={t.value} onClick={() => { setProductTypeFilter(productTypeFilter === t.value ? '' : t.value); setPage(1); }}
                    className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                      productTypeFilter === t.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">카테고리</label>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => { setCategoryFilter(''); setPage(1); }}
                  className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                    categoryFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>전체</button>
                {ITEM_CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => { setCategoryFilter(categoryFilter === c.value ? '' : c.value); setPage(1); }}
                    className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                      categoryFilter === c.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>{c.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => { setStatusFilter(''); setPage(1); }}
                  className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                    statusFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>전체</button>
                {PRODUCT_STATUSES.map(s => (
                  <button key={s.value} onClick={() => { setStatusFilter(statusFilter === s.value ? '' : s.value); setPage(1); }}
                    className={`px-2.5 py-1 rounded-md text-xs transition-colors cursor-pointer ${
                      statusFilter === s.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>{s.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">검색</label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input type="text" placeholder="상품명 / 상품코드 검색" value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>
        </div>

        {/* 카운트 */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600">
            총 <span className="font-semibold text-gray-800">{total}</span>개 상품
          </div>
        </div>

        {/* 상품 목록 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-shopping-bag-line text-5xl block mb-3"></i>등록된 상품이 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((p) => {
              const categoryLabel = ITEM_CATEGORIES.find(c => c.value === p.category)?.label || p.category || '-';
              const statusLabel = PRODUCT_STATUSES.find(s => s.value === p.status)?.label || p.status || '-';
              const statusColor = PRODUCT_STATUS_COLORS[p.status || ''] || 'bg-gray-100 text-gray-700';
              const typeLabel = PRODUCT_TYPES.find(t => t.value === p.productType)?.label || '판매용';
              const typeColor = PRODUCT_TYPE_COLORS[p.productType || 'SALE'] || 'bg-green-100 text-green-700';
              return (
                <div key={p.productIdx}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all group cursor-pointer"
                  onClick={() => openModal(p)}>
                  {/* 이미지 영역 */}
                  <div className="aspect-[3/4] bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center relative group/img">
                    {p.thumbnailUrl ? (
                      <>
                        <img src={p.thumbnailUrl} alt={p.name} className="w-full h-full object-cover" />
                        <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxUrl(p.thumbnailUrl); }}
                          className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center cursor-pointer">
                          <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover/img:opacity-100 transition-opacity drop-shadow-lg"></i>
                        </button>
                      </>
                    ) : (
                      <i className="ri-t-shirt-line text-5xl text-gray-300"></i>
                    )}
                    {/* 상태 뱃지 */}
                    <span className={`absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                      {statusLabel}
                    </span>
                    {/* 유형 뱃지 */}
                    <span className={`absolute top-3 left-3 text-[10px] px-2 py-0.5 rounded-full font-medium ${typeColor}`}>
                      {typeLabel}
                    </span>
                    {/* 카테고리 */}
                    <span className="absolute bottom-3 left-3 text-[10px] px-2 py-0.5 rounded-full bg-white/90 text-gray-600 font-medium backdrop-blur-sm">
                      {categoryLabel}
                    </span>
                  </div>
                  {/* 정보 영역 */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{p.name}</h3>
                        {p.productCode && <p className="text-[10px] text-gray-400 mt-0.5">{p.productCode}</p>}
                      </div>
                    </div>
                    {p.description && <p className="text-xs text-gray-500 mb-3 line-clamp-1">{p.description}</p>}
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-lg font-bold text-gray-900">{getDisplayPrice(p).toLocaleString()}<span className="text-xs font-normal text-gray-500">원</span></div>
                        <div className="text-[10px] text-gray-400">
                          {getPriceLabel(p)} | 공급가 {(p.costPrice || 0).toLocaleString()}원
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          <span className={`font-semibold ${
                            (p.productType === 'RENTAL' ? (p.totalStock ?? 0) : (p.stockQuantity ?? 0)) === 0
                              ? 'text-red-500' : 'text-gray-900'
                          }`}>{getStockDisplay(p)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 액션 바 */}
                  <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); openModal(p); }}
                      className="text-xs text-blue-600 hover:text-blue-800 px-3 py-1.5 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer flex items-center gap-1">
                      <i className="ri-edit-line"></i> 수정
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                      className="text-xs text-red-500 hover:text-red-700 px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex items-center gap-1">
                      <i className="ri-delete-bin-line"></i> 삭제
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* 상품 등록/수정 모달 */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold">{editingProduct ? '상품 수정' : '상품 등록'}</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* 상품 이미지 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-image-line text-gray-400"></i> 상품 이미지
                  </h3>
                  <div className="flex items-start gap-4">
                    <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 overflow-hidden flex items-center justify-center bg-gray-50 flex-shrink-0 relative group">
                      {isUploading && (
                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                      {imagePreview ? (
                        <>
                          <img src={imagePreview} alt="상품 이미지" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setLightboxUrl(imagePreview)}
                            className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center cursor-pointer">
                            <i className="ri-zoom-in-line text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                          </button>
                        </>
                      ) : (
                        <div className="text-center text-gray-400">
                          <i className="ri-image-add-line text-2xl block"></i>
                          <span className="text-[10px]">이미지 없음</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors text-sm font-medium text-gray-700">
                        <i className="ri-upload-2-line"></i> 이미지 선택
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                      {imagePreview && (
                        <button type="button" onClick={() => { setImagePreview(''); setForm(prev => ({ ...prev, thumbnailUrl: '' })); }}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 cursor-pointer">
                          <i className="ri-delete-bin-line"></i> 이미지 삭제
                        </button>
                      )}
                      <p className="text-[11px] text-gray-400">JPG, PNG 형식 / 최대 10MB</p>
                    </div>
                  </div>
                </div>

                {/* 상품 유형 선택 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-gray-400"></i> 상품 유형
                  </h3>
                  <div className="flex gap-3">
                    {PRODUCT_TYPES.map(t => (
                      <button key={t.value} type="button"
                        onClick={() => setForm(prev => ({ ...prev, productType: t.value }))}
                        className={`flex-1 py-3 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer ${
                          form.productType === t.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}>
                        <i className={`block text-lg mb-1 ${
                          t.value === 'SALE' ? 'ri-shopping-bag-line' :
                          t.value === 'RENTAL' ? 'ri-repeat-line' : 'ri-stack-line'
                        }`}></i>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 기본 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-information-line text-gray-400"></i> 기본 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품명 *</label>
                      <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="예: 클래식 블랙 슈트" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">카테고리 *</label>
                      <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {ITEM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {PRODUCT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                      <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder="상품 설명 (선택)" />
                    </div>
                  </div>
                </div>

                {/* 가격/재고 — 유형별 분기 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-money-dollar-circle-line text-gray-400"></i> 가격 / 재고
                  </h3>

                  {form.productType === 'SALE' && (
                    <>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">판매가 *</label>
                          <input type="number" value={form.salesPrice} onChange={(e) => setForm({ ...form, salesPrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">공급가</label>
                          <input type="number" value={form.costPrice} onChange={(e) => setForm({ ...form, costPrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">재고 수량</label>
                          <input type="number" value={form.stockQuantity} onChange={(e) => setForm({ ...form, stockQuantity: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                      </div>
                      {form.salesPrice > 0 && form.costPrice > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg flex items-center gap-4 text-sm">
                          <span className="text-gray-500">마진</span>
                          <span className="font-semibold text-gray-900">{(form.salesPrice - form.costPrice).toLocaleString()}원</span>
                          <span className="text-gray-400">|</span>
                          <span className="text-gray-500">마진율</span>
                          <span className="font-semibold text-blue-600">{((1 - form.costPrice / form.salesPrice) * 100).toFixed(1)}%</span>
                        </div>
                      )}
                    </>
                  )}

                  {form.productType === 'RENTAL' && (
                    <>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">대여료 *</label>
                          <input type="number" value={form.rentalPrice} onChange={(e) => setForm({ ...form, rentalPrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">공급가</label>
                          <input type="number" value={form.costPrice} onChange={(e) => setForm({ ...form, costPrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">보증금</label>
                          <input type="number" value={form.depositAmount} onChange={(e) => setForm({ ...form, depositAmount: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">총 보유수량 *</label>
                          <input type="number" value={form.totalStock} onChange={(e) => setForm({ ...form, totalStock: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                          <p className="text-[11px] text-gray-400 mt-1">물리적으로 보유한 수량 (대여 가능 여부는 날짜 기반으로 판단)</p>
                        </div>
                      </div>
                    </>
                  )}

                  {form.productType === 'PACKAGE' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">패키지가 *</label>
                          <input type="number" value={form.packagePrice} onChange={(e) => setForm({ ...form, packagePrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">공급가</label>
                          <input type="number" value={form.costPrice} onChange={(e) => setForm({ ...form, costPrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* 패키지 구성 상품 (패키지일 때만) */}
                {form.productType === 'PACKAGE' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="ri-stack-line text-gray-400"></i> 구성 상품
                    </h3>

                    {/* 구성 상품 검색 */}
                    <div className="relative mb-3">
                      <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input type="text" placeholder="구성 상품 검색 (상품명 입력)" value={componentSearchQuery}
                          onChange={(e) => { setComponentSearchQuery(e.target.value); setShowComponentDropdown(true); }}
                          onFocus={() => setShowComponentDropdown(true)}
                          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      {showComponentDropdown && componentSearchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {componentSearchResults.map((p) => (
                            <button key={p.productIdx} type="button" onClick={() => addPackageItem(p)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex justify-between items-center cursor-pointer">
                              <div>
                                <span className="font-medium">{p.name}</span>
                                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${PRODUCT_TYPE_COLORS[p.productType || 'SALE']}`}>
                                  {PRODUCT_TYPES.find(t => t.value === p.productType)?.label}
                                </span>
                              </div>
                              <span className="text-gray-400 text-xs">{p.productCode}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 구성 상품 목록 */}
                    {form.packageItems.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                        <i className="ri-add-circle-line text-2xl block mb-2"></i>
                        <p className="text-sm">위 검색창에서 구성 상품을 추가하세요</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {form.packageItems.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate block">{item.name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRODUCT_TYPE_COLORS[(item as any).productType || 'SALE']}`}>
                                {PRODUCT_TYPES.find(t => t.value === (item as any).productType)?.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-[10px] text-gray-500">수량</label>
                              <input type="number" value={item.quantity} min={1}
                                onChange={(e) => updatePackageItemQuantity(idx, Number(e.target.value))}
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center" />
                            </div>
                            <button type="button" onClick={() => togglePackageItemRequired(idx)}
                              className={`text-[10px] px-2 py-1 rounded cursor-pointer ${
                                item.isRequired === 'y' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                              {item.isRequired === 'y' ? '필수' : '선택'}
                            </button>
                            <button type="button" onClick={() => removePackageItem(idx)}
                              className="text-red-400 hover:text-red-600 cursor-pointer">
                              <i className="ri-close-line"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setShowModal(false)}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer">취소</button>
                  <button type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">{editingProduct ? '수정' : '등록'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* 이미지 라이트박스 */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-8" onClick={() => setLightboxUrl('')}>
          <button onClick={() => setLightboxUrl('')}
            className="absolute top-6 right-6 text-white/80 hover:text-white cursor-pointer z-10">
            <i className="ri-close-line text-3xl"></i>
          </button>
          <img src={lightboxUrl} alt="상품 이미지 확대" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
